import React, { useState, useEffect } from 'react';
import {
  Plus, Edit2, Trash2, Search, X, Users as UsersIcon,
  Key, AlertTriangle, CheckCircle, Calendar, CreditCard
} from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const Users = () => {
  const [users,         setUsers]         = useState([]);
  const [employees,     setEmployees]     = useState([]);
  const [userTypes,     setUserTypes]     = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [searchTerm,    setSearchTerm]    = useState('');
  const [showModal,     setShowModal]     = useState(false);
  const [editMode,      setEditMode]      = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showPassword,  setShowPassword]  = useState(false);
  const [errors,        setErrors]        = useState({});
  const [selectedSubInfo, setSelectedSubInfo] = useState(null);

  const [subInfo, setSubInfo] = useState({
    has_subscription: false, max_users: 0, active_count: 0, can_add: false,
    duration_months: 0, subscriptionid: null,
  });

  const [formData, setFormData] = useState({
    userid: null, username: '', password: '', fullname: '', email: '',
    employeeid: '', usertypeid: '', usertype: 'User',
    isactive: true, subscription_expiry: '', subscriptionid: '',
  });

  useEffect(() => { fetchUsers(); fetchEmployees(); fetchUserTypes(); fetchSubscriptions(); }, []);
  useEffect(() => { filterUsers(); }, [searchTerm, users]);

  useEffect(() => {
    if (!formData.subscriptionid || !subscriptions.length) { setSelectedSubInfo(null); return; }
    const sub = subscriptions.find(s => s.subscriptionid == formData.subscriptionid);
    setSelectedSubInfo(sub || null);
  }, [formData.subscriptionid, subscriptions]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/Users.php');
      if (res.data.success) { setUsers(res.data.data || []); if (res.data.subscription_info) setSubInfo(res.data.subscription_info); }
    } catch (e) { if (e.response?.status === 401) alert('Session expired. Please login again.'); }
    finally { setLoading(false); }
  };

  const fetchEmployees = async () => {
    try { const res = await apiClient.get('/Employees.php', { params: { active: true } }); if (res.data.success) setEmployees(res.data.data || []); } catch (e) { console.error(e); }
  };
  const fetchUserTypes = async () => {
    try { const res = await apiClient.get('/UserTypes.php', { params: { active: true } }); if (res.data.success) setUserTypes(res.data.data || []); } catch (e) { console.error(e); }
  };
  const fetchSubscriptions = async () => {
    try {
      const res = await apiClient.get('/Subscriptions.php');
      if (res.data.success) {
        setSubscriptions((res.data.data || []).filter(s => s.isactive));
        if (res.data.summary) setSubInfo(p => ({ ...p, has_subscription: res.data.summary.has_subscription, max_users: res.data.summary.max_users, active_count: res.data.summary.active_count, duration_months: res.data.summary.duration_months }));
      }
    } catch (e) { console.error(e); }
  };

  const filterUsers = () => {
    if (!searchTerm.trim()) { setFilteredUsers(users); return; }
    const q = searchTerm.toLowerCase();
    setFilteredUsers(users.filter(u =>
      u.username?.toLowerCase().includes(q) || u.fullname?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) || u.employeename?.toLowerCase().includes(q)
    ));
  };

  const validateForm = () => {
    const e = {};
    if (!formData.username.trim()) e.username = 'Username is required';
    else if (formData.username.length > 50) e.username = 'Max 50 characters';
    if (!editMode && !formData.password.trim()) e.password = 'Password is required';
    else if (formData.password && formData.password.length < 6) e.password = 'Min 6 characters';
    if (!formData.fullname.trim()) e.fullname = 'Full name is required';
    else if (formData.fullname.length > 100) e.fullname = 'Max 100 characters';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Invalid email';
    if (!editMode && !formData.subscriptionid) e.subscriptionid = 'Please select a subscription';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const payload = { ...formData, employeeid: formData.employeeid !== '' ? parseInt(formData.employeeid) : null, usertypeid: formData.usertypeid !== '' ? parseInt(formData.usertypeid) : null, subscriptionid: formData.subscriptionid !== '' ? parseInt(formData.subscriptionid) : null, isactive: formData.isactive ? 1 : 0 };
      if (editMode && !formData.password) delete payload.password;
      const res = await apiClient[editMode ? 'put' : 'post']('/Users.php', payload);
      if (res.data.success) {
        if (res.data.subscription_info) setSubInfo(res.data.subscription_info);
        alert(res.data.message || (editMode ? 'User updated!' : 'User created!'));
        setShowModal(false); resetForm(); fetchUsers(); fetchSubscriptions();
      } else { alert('Error: ' + res.data.message); }
    } catch (error) {
      if (error.response?.status === 401)  alert('Session expired. Please login again.');
      else if (error.response?.status === 403) alert(error.response.data.message);
      else if (error.response?.status === 409) alert('Username already exists.');
      else alert('Error saving user. Please try again.');
    } finally { setSubmitLoading(false); }
  };

  const handleEdit = (user) => {
    setFormData({ userid: user.userid, username: user.username, password: '', fullname: user.fullname, email: user.email || '', employeeid: user.employeeid || '', usertypeid: user.usertypeid || '', usertype: user.usertype || 'User', isactive: user.isactive === 1, subscription_expiry: user.subscription_expiry || '', subscriptionid: user.subscriptionid || '' });
    setEditMode(true); setShowModal(true); setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      const res = await apiClient.delete('/Users.php', { data: { userid: id } });
      if (res.data.success) { if (res.data.subscription_info) setSubInfo(res.data.subscription_info); fetchUsers(); fetchSubscriptions(); }
      else alert('Error: ' + res.data.message);
    } catch (error) {
      if (error.response?.status === 400) alert('Cannot delete your own account.');
      else alert('Error deleting user.');
    }
  };

  const resetForm = () => {
    setFormData({ userid: null, username: '', password: '', fullname: '', email: '', employeeid: '', usertypeid: '', usertype: 'User', isactive: true, subscription_expiry: '', subscriptionid: '' });
    setSelectedSubInfo(null); setEditMode(false); setErrors({}); setShowPassword(false);
  };

  const openAddModal = () => {
    if (!subscriptions.length) { alert('No active subscriptions found.\n\nPlease go to Subscription Management and purchase a subscription before adding users.'); return; }
    resetForm(); setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); resetForm(); };

  const getLimitColor = () => {
    if (!subInfo.has_subscription) return '#dc2626';
    const pct = subInfo.active_count / subInfo.max_users;
    return pct >= 1 ? '#dc2626' : pct >= 0.8 ? '#f59e0b' : '#16a34a';
  };

  const fmtDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
  };

  const today = new Date().toISOString().split('T')[0];

  const expiryPreview = (() => {
    if (!selectedSubInfo?.duration_months) return null;
    const d = new Date(); d.setMonth(d.getMonth() + selectedSubInfo.duration_months);
    return d.toISOString().split('T')[0];
  })();

  const selectedSubSlots = selectedSubInfo ? { used: selectedSubInfo.users_linked ?? 0, max: selectedSubInfo.max_users, left: selectedSubInfo.slots_left ?? selectedSubInfo.max_users } : null;
  const canAddToSelected = selectedSubSlots ? selectedSubSlots.left > 0 : false;
  const lc = getLimitColor();

  const inputCls  = (err, readonly) => `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium outline-none transition-all duration-200 ${readonly ? 'bg-gray-100 cursor-not-allowed' : 'bg-white focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)]'} ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;
  const selectCls = `w-full px-3.5 py-3 border-2 border-gray-200 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)] cursor-pointer`;
  const labelCls  = 'block mb-1.5 text-xs font-bold text-gray-600 uppercase tracking-wide';
  const errMsg    = (msg) => msg ? <p className="text-xs text-red-600 font-semibold mt-1">⚠ {msg}</p> : null;

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <UsersIcon size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Users Master</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage user accounts and access control</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Subscription status badge */}
          <div className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-bold"
            style={{ background: lc + '18', border: `1.5px solid ${lc}`, color: lc }}>
            {subInfo.has_subscription
              ? <><CheckCircle size={13} /> {subInfo.active_count}/{subInfo.max_users} users</>
              : <><AlertTriangle size={13} /> No subscription</>}
          </div>
          <button onClick={openAddModal} disabled={!subscriptions.length}
            className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ color: NAVY }} title={!subscriptions.length ? 'No active subscriptions' : 'Add new user'}>
            <Plus size={17} /> Add User
          </button>
        </div>
      </div>

      {/* No-subscription warning */}
      {!subscriptions.length && !loading && (
        <div className="flex items-center gap-3 mx-5 mt-4 px-4 py-3.5 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-medium">
          <AlertTriangle size={18} className="flex-shrink-0" />
          <span>
            <strong>No active subscriptions.</strong> You cannot add new users until a subscription is purchased.
            Please go to <strong>Subscription Management</strong> to purchase user slots.
          </span>
        </div>
      )}

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0 mt-0">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search by username, name, email, or employee..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          Total: {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'}
        </div>
      </div>

      {/* ── TABLE ── */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">Loading users...</div>
        ) : filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <UsersIcon size={40} className="text-gray-300 mb-4" />
            <p className="text-xl text-gray-400 font-semibold mb-3">No users found</p>
            {searchTerm && (
              <button onClick={() => setSearchTerm('')}
                className="text-sm text-blue-600 underline cursor-pointer bg-transparent border-0">Clear search</button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['Username', 'Full Name', 'Email', 'Employee', 'User Type', 'Status', 'Subscription', 'Expiry', 'Last Login', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                      style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.userid}
                    className={`border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b] ${user.sub_expired ? 'bg-red-50/40' : ''}`}>
                    <td className="px-4 py-3.5 font-semibold text-gray-800">{user.username}</td>
                    <td className="px-4 py-3.5 text-gray-700">{user.fullname}</td>
                    <td className="px-4 py-3.5 text-sm text-gray-600">{user.email || '-'}</td>
                    <td className="px-4 py-3.5">
                      {user.employeename
                        ? <span className="inline-block px-2.5 py-1 bg-blue-50 text-blue-700 border-2 border-blue-200 rounded-lg text-[11px] font-bold">{user.employeecode} — {user.employeename}</span>
                        : <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="inline-block px-2.5 py-1 bg-violet-100 text-violet-700 border-2 border-violet-200 rounded-lg text-[11px] font-bold">
                        {user.usertypename || user.usertype || 'User'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide border-2 ${
                        user.isactive ? 'bg-emerald-50 text-emerald-700 border-emerald-400' : 'bg-red-50 text-red-700 border-red-400'
                      }`}>
                        {user.isactive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-500">
                      {user.subscriptionid
                        ? <span className="font-mono text-[11px] font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded">Sub #{user.subscriptionid}</span>
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3.5">
                      {user.subscription_expiry ? (
                        <div>
                          <span className={`text-sm font-medium ${user.sub_expired ? 'text-red-600 font-semibold' : user.sub_days_left <= 7 ? 'text-amber-600' : 'text-gray-700'}`}>
                            {fmtDate(user.subscription_expiry)}
                          </span>
                          {user.sub_expired
                            ? <div className="text-[11px] text-red-600 mt-0.5">⚠️ Expired</div>
                            : user.sub_days_left <= 7
                              ? <div className="text-[11px] text-amber-600 mt-0.5">{user.sub_days_left}d left</div>
                              : null}
                        </div>
                      ) : <span className="text-gray-400 text-sm">—</span>}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-500">
                      {user.lastlogin ? new Date(user.lastlogin).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEdit(user)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(user.userid)} title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4"
          style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="bg-white rounded-2xl w-full max-w-[640px] shadow-2xl border border-gray-200 overflow-hidden max-h-[92vh] flex flex-col"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between px-7 py-5 flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                <UsersIcon size={18} /> {editMode ? 'Edit User' : 'Add New User'}
              </h3>
              <button onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-7">

              {/* ── Subscription selector (add mode) ── */}
              {!editMode && (
                <div className="mb-5">
                  <label className="flex items-center gap-1.5 mb-3 text-xs font-bold text-gray-600 uppercase tracking-wide">
                    <CreditCard size={13} /> Select Subscription <span className="text-red-500">*</span>
                  </label>

                  {subscriptions.length === 0 ? (
                    <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      <AlertTriangle size={14} className="flex-shrink-0" /> No active subscriptions available.
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2.5">
                      {subscriptions.map(sub => {
                        const slotsLeft = sub.slots_left ?? (sub.max_users - (sub.users_linked ?? 0));
                        const isFull   = slotsLeft <= 0;
                        const isChosen = formData.subscriptionid == sub.subscriptionid;
                        return (
                          <label key={sub.subscriptionid}
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer border-2 transition-all duration-150 ${
                              isChosen ? 'border-violet-600 bg-violet-50' : isFull ? 'border-gray-200 bg-gray-50 opacity-55' : 'border-gray-200 bg-white hover:border-violet-300'
                            } ${isFull ? 'cursor-not-allowed' : ''}`}>
                            <input type="radio" name="subscriptionid" value={sub.subscriptionid}
                              checked={isChosen} disabled={isFull}
                              onChange={() => setFormData(f => ({ ...f, subscriptionid: sub.subscriptionid }))}
                              className="accent-violet-600" />
                            <div className="flex-1">
                              <div className={`font-bold text-[13px] ${isFull ? 'text-gray-400' : 'text-gray-800'}`}>
                                {sub.max_users} Users · {sub.duration_months} Month{sub.duration_months > 1 ? 's' : ''}
                                {sub.currency && sub.amount_paid && (
                                  <span className="font-normal text-gray-500 ml-2 text-[12px]">
                                    ({sub.currency} {parseFloat(sub.amount_paid).toFixed(2)})
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] text-gray-500 mt-0.5">
                                Sub #{sub.subscriptionid} · Payment: {sub.payment_mode || '—'}
                              </div>
                            </div>
                            <div className="text-right min-w-[72px]">
                              <span className={`inline-block px-2.5 py-0.5 rounded-full text-[12px] font-bold ${isFull ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'}`}>
                                {slotsLeft} left
                              </span>
                              <div className="text-[10px] text-gray-400 mt-1">{sub.users_linked ?? 0}/{sub.max_users} used</div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  {errMsg(errors.subscriptionid)}
                </div>
              )}

              {/* Selected sub preview */}
              {!editMode && selectedSubInfo && (
                <div className={`flex items-start gap-2.5 mb-4 px-4 py-3 rounded-lg text-[13px] font-medium ${
                  canAddToSelected ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'
                }`}>
                  {canAddToSelected
                    ? <><CheckCircle size={15} className="flex-shrink-0 mt-0.5" />
                        <span>Slot {(selectedSubInfo.users_linked ?? 0) + 1} of {selectedSubInfo.max_users} · This user will expire on <strong>{fmtDate(expiryPreview)}</strong> ({selectedSubInfo.duration_months} month{selectedSubInfo.duration_months > 1 ? 's' : ''} from today)</span>
                      </>
                    : <><AlertTriangle size={15} className="flex-shrink-0 mt-0.5" /><span>This subscription is full — no slots remaining.</span></>
                  }
                </div>
              )}

              {/* Edit mode: current sub + expiry */}
              {editMode && (
                <div className="mb-5 px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm">
                  <div className="flex items-center gap-2 font-bold text-gray-600 mb-3">
                    <Calendar size={14} /> Subscription &amp; Expiry
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`font-semibold ${formData.subscription_expiry < today ? 'text-red-600' : 'text-emerald-700'}`}>
                      {formData.subscription_expiry
                        ? formData.subscription_expiry < today
                          ? `⚠ Expired on ${fmtDate(formData.subscription_expiry)}`
                          : `Active until ${fmtDate(formData.subscription_expiry)}`
                        : 'No expiry set'}
                    </span>
                    <input type="date" value={formData.subscription_expiry}
                      onChange={e => setFormData({ ...formData, subscription_expiry: e.target.value })}
                      className="ml-auto text-[12px] px-2.5 py-1.5 border border-gray-200 rounded-lg outline-none focus:border-[#0d1c6b]"
                      title="Admin override: change expiry date" />
                  </div>
                  {formData.subscriptionid && (
                    <div className="text-[11px] text-gray-400 mt-2">Linked to Sub #{formData.subscriptionid}</div>
                  )}
                </div>
              )}

              {/* Username + Password */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelCls}>Username <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.username} onChange={e => setFormData({...formData, username: e.target.value})}
                    maxLength="50" placeholder="e.g., john.doe" readOnly={editMode} className={inputCls(errors.username, editMode)} />
                  {errMsg(errors.username)}
                </div>
                <div>
                  <label className={labelCls}>Password {!editMode && <span className="text-red-500">*</span>}</label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} value={formData.password}
                      onChange={e => setFormData({...formData, password: e.target.value})}
                      placeholder={editMode ? 'Leave blank to keep current' : 'Enter password'}
                      className={inputCls(errors.password, false) + ' pr-10'} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-0 cursor-pointer text-gray-400 hover:text-gray-600 p-0">
                      <Key size={15} />
                    </button>
                  </div>
                  {errMsg(errors.password)}
                </div>
              </div>

              {/* Full name + Email */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelCls}>Full Name <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.fullname} onChange={e => setFormData({...formData, fullname: e.target.value})}
                    maxLength="100" placeholder="e.g., John Doe" className={inputCls(errors.fullname, false)} />
                  {errMsg(errors.fullname)}
                </div>
                <div>
                  <label className={labelCls}>Email</label>
                  <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})}
                    maxLength="100" placeholder="e.g., john@example.com" className={inputCls(errors.email, false)} />
                  {errMsg(errors.email)}
                </div>
              </div>

              {/* Employee + User Type */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelCls}>Employee</label>
                  <select value={formData.employeeid} onChange={e => setFormData({...formData, employeeid: e.target.value})} className={selectCls}>
                    <option value="">-- No Employee Link --</option>
                    {employees.map(emp => <option key={emp.employeeid} value={emp.employeeid}>{emp.employeecode} — {emp.employeename}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>User Type</label>
                  <select value={formData.usertypeid} onChange={e => setFormData({...formData, usertypeid: e.target.value})} className={selectCls}>
                    <option value="">-- Select User Type --</option>
                    {userTypes.map(t => <option key={t.usertypeid} value={t.usertypeid}>{t.usertypename}</option>)}
                  </select>
                </div>
              </div>

              {/* Active */}
              <div className="mb-6">
                <label className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all duration-150">
                  <input type="checkbox" checked={formData.isactive} onChange={e => setFormData({...formData, isactive: e.target.checked})}
                    className="w-5 h-5 cursor-pointer accent-[#0d1c6b]" />
                  <span className="text-sm font-semibold text-gray-700">Active</span>
                </label>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={closeModal} disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={submitLoading || (!editMode && (!formData.subscriptionid || !canAddToSelected))}
                  className="px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  {submitLoading ? 'Saving...' : editMode ? 'Update' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default Users;